// ===========================================
// EtatReglements — Tableau de bord comptable (refonte UX cohérente)
// 3 tabs : par contrat / par client / par période
// ===========================================
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Card, Tabs, Table, Select, Input, Button, DatePicker,
    Tag, Dropdown, Typography, Progress, Tooltip, Modal,
} from 'antd';
import {
    DownloadOutlined, FilePdfOutlined, FileExcelOutlined,
    SearchOutlined, FileProtectOutlined, TeamOutlined, CalendarOutlined, ReloadOutlined,
    CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined,
    UserOutlined, BankOutlined, PercentageOutlined, EyeOutlined, FundOutlined,
    DollarOutlined, ClearOutlined, QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { reglementsService, agencesService, compagniesService, typesAssuranceService } from '../../common/services';
import { useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant as formatMontantBase, formatPhone as formatPhoneShared } from '../../common/utils/format';
import TermHelp from '../../components/termHelp';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './etat-reglements.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const formatMontant = (val) => formatMontantBase(val, { fallback: '—' });
const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '' });

const EtatReglements = () => {
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('par-contrat');
    const [guideOpen, setGuideOpen] = useState(false);

    // ─── Filtres partagés ─────────────────────────────────────────────
    const [agences, setAgences] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [typesAssurance, setTypesAssurance] = useState([]);
    const [selectedAgence, setSelectedAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [selectedCompagnie, setSelectedCompagnie] = useState('all');
    const [selectedType, setSelectedType] = useState('all');
    const [selectedStatut, setSelectedStatut] = useState('all');
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(null);

    // Par contrat
    const [contratData, setContratData] = useState([]);
    const [contratStats, setContratStats] = useState({});
    const [contratPagination, setContratPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [contratLoading, setContratLoading] = useState(false);

    // Par client
    const [clientData, setClientData] = useState([]);
    const [clientStats, setClientStats] = useState({});
    const [clientPagination, setClientPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [clientLoading, setClientLoading] = useState(false);

    // Par période
    const [periodes, setPeriodes] = useState([]);
    const [periodeStats, setPeriodeStats] = useState({});
    const [periodeLoading, setPeriodeLoading] = useState(false);
    const [groupBy, setGroupBy] = useState('mois');

    useEffect(() => {
        Promise.all([
            agencesService.getAll().catch(() => []),
            compagniesService.getAll().catch(() => []),
            typesAssuranceService.getAll().catch(() => []),
        ]).then(([agRes, compRes, typeRes]) => {
            setAgences(Array.isArray(agRes) ? agRes : agRes?.data || []);
            setCompagnies(Array.isArray(compRes) ? compRes : compRes?.data || []);
            setTypesAssurance(Array.isArray(typeRes) ? typeRes : typeRes?.data || []);
        });
    }, []);

    const buildParams = () => {
        const params = {};
        if (selectedAgence !== 'all') params.agence = selectedAgence;
        if (selectedStatut !== 'all') params.statut = selectedStatut;
        if (selectedCompagnie !== 'all') params.compagnie = selectedCompagnie;
        if (selectedType !== 'all') params.typeAssurance = selectedType;
        if (search) params.q = search;
        if (dateRange?.[0] && dateRange?.[1]) {
            params.start = dateRange[0].format('YYYY-MM-DD');
            params.end = dateRange[1].format('YYYY-MM-DD');
        }
        return params;
    };

    // ─── Fetchs ───────────────────────────────────────────────────────
    const fetchContrats = async (page = 1, pageSize = 20) => {
        setContratLoading(true);
        try {
            const params = { ...buildParams(), page, limit: pageSize };
            const res = await reglementsService.etatParContrat(params);
            const d = res?.data || res;
            setContratData(d?.items || []);
            setContratStats(d?.stats || {});
            setContratPagination({ current: d?.page || page, pageSize, total: d?.total || 0 });
        } catch (e) { toast.error('Erreur chargement'); }
        finally { setContratLoading(false); }
    };

    const fetchClients = async (page = 1, pageSize = 20) => {
        setClientLoading(true);
        try {
            const params = { ...buildParams(), page, limit: pageSize };
            const res = await reglementsService.etatParClient(params);
            const d = res?.data || res;
            setClientData(d?.items || []);
            setClientStats(d?.stats || {});
            setClientPagination({ current: d?.page || page, pageSize, total: d?.total || 0 });
        } catch (e) { toast.error('Erreur chargement'); }
        finally { setClientLoading(false); }
    };

    const fetchPeriodes = async () => {
        setPeriodeLoading(true);
        try {
            const params = { groupBy };
            if (selectedAgence !== 'all') params.agence = selectedAgence;
            if (selectedCompagnie !== 'all') params.compagnie = selectedCompagnie;
            if (selectedType !== 'all') params.typeAssurance = selectedType;
            if (dateRange?.[0] && dateRange?.[1]) {
                params.start = dateRange[0].format('YYYY-MM-DD');
                params.end = dateRange[1].format('YYYY-MM-DD');
            } else {
                const now = new Date();
                params.start = `${now.getFullYear()}-01-01`;
                params.end = now.toISOString().split('T')[0];
            }
            const res = await reglementsService.etatParPeriode(params);
            const d = res?.data || res;
            setPeriodes(d?.periodes || []);
            setPeriodeStats(d?.stats || {});
        } catch (e) { toast.error('Erreur chargement'); }
        finally { setPeriodeLoading(false); }
    };

    const fetchCurrent = () => {
        if (activeTab === 'par-contrat') fetchContrats();
        else if (activeTab === 'par-client') fetchClients();
        else fetchPeriodes();
    };

    useEffect(() => {
        fetchCurrent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, selectedAgence, selectedCompagnie, selectedType, selectedStatut, dateRange, groupBy]);

    useEffect(() => {
        const t = setTimeout(() => fetchCurrent(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const currentStats = activeTab === 'par-contrat' ? contratStats : activeTab === 'par-client' ? clientStats : {};
    const tauxRecouvrement = currentStats.totalPrimeTTC > 0
        ? Math.round((currentStats.totalVerse / currentStats.totalPrimeTTC) * 100)
        : 0;

    // ─── Filtres actifs ───────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (selectedStatut !== 'all') n++;
        if (selectedAgence !== 'all') n++;
        if (selectedCompagnie !== 'all') n++;
        if (selectedType !== 'all') n++;
        if (dateRange) n++;
        return n;
    }, [selectedStatut, selectedAgence, selectedCompagnie, selectedType, dateRange]);

    const clearAllFilters = () => {
        setSearch('');
        setSelectedStatut('all');
        setSelectedAgence('all');
        setSelectedCompagnie('all');
        setSelectedType('all');
        setDateRange(null);
    };

    const getTypeNom = () => (selectedType !== 'all'
        ? (typesAssurance.find((t) => t._id === selectedType)?.nom || '')
        : 'TOUS');

    // ─── Colonnes Par contrat ────────────────────────────────────────
    const contratColumns = [
        {
            title: 'Client',
            key: 'client',
            width: 200,
            render: (_, r) => {
                const c = r.client;
                if (!c) return '—';
                const isE = c.type === 'entreprise';
                const name = c.raisonSociale || `${c.nom || ''} ${c.prenom || ''}`.trim() || '—';
                return (
                    <div className="etat-cell-client">
                        <div className={`etat-cell-avatar ${isE ? 'is-entreprise' : 'is-particulier'}`}>
                            {isE ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <div className="etat-cell-client-body">
                            <div className="etat-cell-name">{name}</div>
                            <div className="etat-cell-phone">{formatPhone(c.telephone)}</div>
                        </div>
                    </div>
                );
            },
            ellipsis: true,
        },
        {
            title: <span>N° Police<TermHelp term="numeroPolice" size={11} /></span>,
            dataIndex: 'numeroPolice',
            key: 'police',
            width: 150,
            render: (v, r) => (
                <button
                    type="button"
                    className="etat-cell-police"
                    onClick={() => navigate(`/dashboard/contrat/${r._id}`)}
                >
                    {v || '—'}
                </button>
            ),
        },
        {
            title: 'Compagnie',
            key: 'comp',
            width: 120,
            render: (_, r) => <span className="etat-cell-soft">{r.compagnie?.nom || '—'}</span>,
            ellipsis: true,
            responsive: ['lg'],
        },
        {
            title: 'Type assurance',
            key: 'typeAssurance',
            width: 150,
            // Libellé court (sans « Assurance ») et tronqué : le nom complet reste au survol
            render: (_, r) => (r.typeAssurance?.nom ? (
                <Tooltip title={r.typeAssurance.nom}>
                    <Tag
                        color="blue"
                        style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}
                    >
                        {r.typeAssurance.nom.replace(/^Assurance\s+/i, '')}
                    </Tag>
                </Tooltip>
            ) : '—'),
            ellipsis: true,
            responsive: ['lg'],
        },
        {
            title: <span>Prime TTC<TermHelp term="primeTTC" size={11} /></span>,
            dataIndex: 'primeTTC',
            key: 'prime',
            width: 110,
            align: 'right',
            sorter: (a, b) => (a.primeTTC || 0) - (b.primeTTC || 0),
            render: (v) => <span className="etat-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: <span>Versé<TermHelp term="totalVerse" size={11} /></span>,
            dataIndex: 'totalVerse',
            key: 'verse',
            width: 110,
            align: 'right',
            sorter: (a, b) => (a.totalVerse || 0) - (b.totalVerse || 0),
            render: (v) => <span className="etat-amount is-positive">{formatMontant(v || 0)}</span>,
        },
        {
            title: <span>Reste<TermHelp term="resteAPayer" size={11} /></span>,
            dataIndex: 'resteAPayer',
            key: 'reste',
            width: 110,
            align: 'right',
            sorter: (a, b) => (a.resteAPayer || 0) - (b.resteAPayer || 0),
            render: (v) => {
                const r = Math.max(0, v);
                return <span className={`etat-amount ${r > 0 ? 'is-warning' : 'is-positive'}`}>{formatMontant(r)}</span>;
            },
        },
        {
            title: <span>Commission<TermHelp term="commission" size={11} /></span>,
            dataIndex: 'commissionCourtier',
            key: 'comm',
            width: 110,
            align: 'right',
            render: (v, r) => (
                <Tooltip title={r.tauxCommission ? `Taux : ${r.tauxCommission}%` : ''}>
                    <span className="etat-amount is-purple">{formatMontant(v || 0)}</span>
                </Tooltip>
            ),
            responsive: ['md'],
        },
        {
            title: 'Statut',
            key: 'statut',
            width: 100,
            align: 'center',
            render: (_, r) => {
                const p = Math.round(r.progressPaiement || 0);
                if (p >= 100) return <Tag color="success" icon={<CheckCircleOutlined />}>Payé</Tag>;
                if (p > 0) return <Tag color="warning" icon={<ExclamationCircleOutlined />}>Partiel</Tag>;
                return <Tag color="error" icon={<CloseCircleOutlined />}>Impayé</Tag>;
            },
        },
        {
            title: '%',
            key: 'pct',
            width: 60,
            align: 'center',
            render: (_, r) => {
                const p = Math.round(r.progressPaiement || 0);
                return (
                    <Progress
                        type="circle"
                        percent={p}
                        size={36}
                        strokeColor={p >= 100 ? '#16a34a' : p > 0 ? '#1A2B5F' : '#C8102E'}
                    />
                );
            },
        },
    ];

    // ─── Colonnes Par client ──────────────────────────────────────────
    const clientColumns = [
        {
            title: 'Client',
            key: 'client',
            width: 280,
            render: (_, r) => {
                const c = r.client;
                if (!c) return '—';
                const isE = c.type === 'entreprise';
                const name = c.raisonSociale || `${c.nom || ''} ${c.prenom || ''}`.trim() || '—';
                return (
                    <div className="etat-cell-client">
                        <div className={`etat-cell-avatar ${isE ? 'is-entreprise' : 'is-particulier'}`}>
                            {isE ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <div className="etat-cell-client-body">
                            <div className="etat-cell-name">{name}</div>
                            <div className="etat-cell-phone">{formatPhone(c.telephone)}</div>
                            <Tag color={isE ? 'orange' : 'blue'} className="etat-cell-type">
                                {isE ? 'Entreprise' : 'Particulier'}
                            </Tag>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Contrats',
            dataIndex: 'nbContrats',
            key: 'nb',
            width: 100,
            align: 'center',
            render: (v) => (
                <span className={`etat-cell-nb ${v > 0 ? 'has-contrats' : ''}`}>{v || 0}</span>
            ),
        },
        {
            title: 'Total primes',
            dataIndex: 'totalPrimeTTC',
            key: 'primes',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.totalPrimeTTC || 0) - (b.totalPrimeTTC || 0),
            render: (v) => <span className="etat-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Versé',
            dataIndex: 'totalVerse',
            key: 'verse',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.totalVerse || 0) - (b.totalVerse || 0),
            render: (v) => <span className="etat-amount is-positive">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Reste',
            dataIndex: 'resteAPayer',
            key: 'reste',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.resteAPayer || 0) - (b.resteAPayer || 0),
            render: (v) => {
                const r = Math.max(0, v);
                return <span className={`etat-amount ${r > 0 ? 'is-warning' : 'is-positive'}`}>{formatMontant(r)}</span>;
            },
        },
        {
            title: 'Progression',
            key: 'prog',
            width: 150,
            align: 'center',
            render: (_, r) => {
                const p = Math.round(r.progressPaiement || 0);
                return (
                    <Progress
                        percent={p}
                        size="small"
                        strokeColor={p >= 100 ? '#16a34a' : p > 0 ? '#1A2B5F' : '#C8102E'}
                        style={{ width: 100, margin: 0 }}
                    />
                );
            },
            responsive: ['md'],
        },
        {
            title: 'Actions',
            key: 'action',
            width: 130,
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    className="etat-action-detail"
                    onClick={() => navigate(`/dashboard/detail-reglement/${r.client?._id}`)}
                >
                    Détail
                </Button>
            ),
        },
    ];

    // ─── Colonnes Par période ─────────────────────────────────────────
    const periodeColumns = [
        {
            title: 'Période',
            dataIndex: 'periode',
            key: 'p',
            width: 130,
            render: (v) => <span className="etat-cell-name">{v}</span>,
        },
        {
            title: 'Nb',
            dataIndex: 'count',
            key: 'c',
            width: 80,
            align: 'center',
            render: (v) => (
                <span className={`etat-cell-nb ${v > 0 ? 'has-contrats' : ''}`}>{v || 0}</span>
            ),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 't',
            width: 150,
            align: 'right',
            render: (v) => <span className="etat-amount is-positive">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Espèces',
            key: 'e',
            width: 130,
            align: 'right',
            render: (_, r) => <span className="etat-amount-soft">{formatMontant(r.parMode?.especes?.total || 0)}</span>,
        },
        {
            title: 'Chèque',
            key: 'ch',
            width: 130,
            align: 'right',
            render: (_, r) => <span className="etat-amount-soft">{formatMontant(r.parMode?.cheque?.total || 0)}</span>,
        },
        {
            title: 'Virement',
            key: 'v',
            width: 130,
            align: 'right',
            render: (_, r) => <span className="etat-amount-soft">{formatMontant(r.parMode?.virement?.total || 0)}</span>,
        },
        {
            title: 'Mobile Money',
            key: 'm',
            width: 140,
            align: 'right',
            render: (_, r) => <span className="etat-amount-soft">{formatMontant(r.parMode?.mobile_money?.total || 0)}</span>,
        },
    ];

    // ─── Exports (logique préservée) ──────────────────────────────────
    const fetchAllForExport = async () => {
        const params = { ...buildParams(), page: 1, limit: 99999 };
        try {
            if (activeTab === 'par-contrat') {
                const res = await reglementsService.etatParContrat(params);
                return (res?.data || res)?.items || [];
            }
            if (activeTab === 'par-client') {
                const res = await reglementsService.etatParClient(params);
                return (res?.data || res)?.items || [];
            }
            return [];
        } catch (e) {
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const exportPDF = async () => {
        const allRows = (activeTab === 'par-contrat' || activeTab === 'par-client') ? await fetchAllForExport() : null;
        const exportContratData = allRows || contratData;
        const exportClientData = allRows || clientData;

        const doc = new jsPDF({ orientation: 'landscape' });
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        try { doc.addImage(logoCap, 'JPEG', 14, 8, 30, 14); } catch (e) { /**/ }
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('ÉTAT DES RÈGLEMENTS', centerX, 18, { align: 'center' });
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')}`, centerX, 24, { align: 'center' });

        // ─── Métadonnées : Agence / Compagnie / Période ────────────────
        const agenceNom = resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId, allLabel: 'TOUTES' });
        const compagnieNom = selectedCompagnie !== 'all'
            ? (compagnies.find((c) => c._id === selectedCompagnie)?.nom || '')
            : 'TOUTES';
        const hasPeriode = dateRange && dateRange[0] && dateRange[1];

        let pdfStartY = 30;
        doc.setFontSize(9);
        let metaY = 30;
        // Agence + Compagnie sur une ligne
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0);
        const metaLeft = `AGENCE : ${agenceNom}`;
        const metaRight = `COMPAGNIE : ${compagnieNom}   |   TYPE : ${getTypeNom()}`;
        doc.text(metaLeft, 14, metaY);
        doc.text(metaRight, pageWidth - 14, metaY, { align: 'right' });

        if (hasPeriode) {
            metaY += 6;
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, metaY);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), metaY);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), metaY);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), metaY);
            doc.setTextColor(0);
        }
        doc.setFont(undefined, 'normal');
        pdfStartY = metaY + 6;

        const fmtPdf = (v) => formatMontant(v).replace(/\s/g, ' ');
        const sumOf = (arr, fn) => arr.reduce((s, r) => s + (Number(fn(r)) || 0), 0);
        const totalCellStyle = { halign: 'right', fontStyle: 'bold', fillColor: false, textColor: [0, 0, 0] };
        const baseStyles = { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3 };
        const baseHeadStyles = PDF_HEAD_STYLES;

        if (activeTab === 'par-contrat') {
            const nomCt = (r) => (r.client?.type === 'entreprise'
                ? (r.client?.raisonSociale || r.client?.nom || '')
                : `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim()) || '';

            // Regrouper les contrats par client (ordre de 1re apparition) ; le nom
            // du client est fusionné (rowSpan) sur toutes les lignes de ses contrats.
            const groupes = [];
            const idxParClient = new Map();
            exportContratData.forEach((r) => {
                const key = (r.client?._id || r.client || nomCt(r)).toString();
                if (!idxParClient.has(key)) { idxParClient.set(key, groupes.length); groupes.push([]); }
                groupes[idxParClient.get(key)].push(r);
            });
            const bodyRows = [];
            groupes.forEach((groupe) => {
                groupe.forEach((r, i) => {
                    const row = [];
                    if (i === 0) row.push({ content: nomCt(r), rowSpan: groupe.length, styles: { valign: 'middle' } });
                    row.push(
                        r.numeroPolice, r.compagnie?.nom || '', r.typeAssurance?.nom || '',
                        fmtPdf(r.primeTTC), fmtPdf(r.totalVerse),
                        fmtPdf(Math.max(0, r.resteAPayer)), fmtPdf(r.commissionCourtier || 0),
                    );
                    bodyRows.push(row);
                });
            });

            autoTable(doc, {
                startY: pdfStartY,
                head: [['Assurés', 'N° Police', 'Compagnie', 'Type assurance', 'Prime TTC', 'Versé', 'Reste', 'Commission']],
                body: bodyRows,
                foot: [[
                    { content: 'TOTAL', colSpan: 4, styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportContratData, (r) => r.primeTTC)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportContratData, (r) => r.totalVerse)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportContratData, (r) => Math.max(0, r.resteAPayer))), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportContratData, (r) => r.commissionCourtier || 0)), styles: totalCellStyle },
                ]],
                styles: { ...baseStyles, fillColor: false }, headStyles: baseHeadStyles,
                alternateRowStyles: { fillColor: false },
                columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
                margin: { bottom: 20 },
                showFoot: 'lastPage',
            });
        } else if (activeTab === 'par-client') {
            autoTable(doc, {
                startY: pdfStartY,
                head: [['Client', 'Type', 'Contrats', 'Total primes', 'Versé', 'Reste']],
                body: exportClientData.map((r) => [
                    `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim(),
                    r.client?.type === 'entreprise' ? 'Entreprise' : 'Particulier',
                    r.nbContrats, fmtPdf(r.totalPrimeTTC), fmtPdf(r.totalVerse), fmtPdf(Math.max(0, r.resteAPayer)),
                ]),
                foot: [[
                    { content: 'TOTAL', colSpan: 2, styles: totalCellStyle },
                    { content: String(sumOf(exportClientData, (r) => r.nbContrats)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportClientData, (r) => r.totalPrimeTTC)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportClientData, (r) => r.totalVerse)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(exportClientData, (r) => Math.max(0, r.resteAPayer))), styles: totalCellStyle },
                ]],
                styles: baseStyles, headStyles: baseHeadStyles,
                alternateRowStyles: { fillColor: [245, 247, 250] },
                columnStyles: { 2: { halign: 'center' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
                margin: { bottom: 20 },
                showFoot: 'lastPage',
            });
        } else {
            autoTable(doc, {
                startY: pdfStartY,
                head: [['Période', 'Nb', 'Total', 'Espèces', 'Chèque', 'Virement', 'Mobile Money']],
                body: periodes.map((r) => [
                    r.periode, r.count, fmtPdf(r.total),
                    fmtPdf(r.parMode?.especes?.total || 0),
                    fmtPdf(r.parMode?.cheque?.total || 0),
                    fmtPdf(r.parMode?.virement?.total || 0),
                    fmtPdf(r.parMode?.mobile_money?.total || 0),
                ]),
                foot: [[
                    { content: 'TOTAL', styles: totalCellStyle },
                    { content: String(sumOf(periodes, (r) => r.count)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(periodes, (r) => r.total)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(periodes, (r) => r.parMode?.especes?.total || 0)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(periodes, (r) => r.parMode?.cheque?.total || 0)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(periodes, (r) => r.parMode?.virement?.total || 0)), styles: totalCellStyle },
                    { content: fmtPdf(sumOf(periodes, (r) => r.parMode?.mobile_money?.total || 0)), styles: totalCellStyle },
                ]],
                styles: baseStyles, headStyles: baseHeadStyles,
                alternateRowStyles: { fillColor: [245, 247, 250] },
                columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
                margin: { bottom: 20 },
                showFoot: 'lastPage',
            });
        }

        addExporterFooter(doc, exporterName);
        doc.save(`etat_reglements_${activeTab}_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF exporté');
    };

    const exportExcel = async () => {
        const allRows = (activeTab === 'par-contrat' || activeTab === 'par-client') ? await fetchAllForExport() : null;
        const exportContratData = allRows || contratData;
        const exportClientData = allRows || clientData;

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Etat');
        let headers, rows, groupSizes = null;
        if (activeTab === 'par-contrat') {
            headers = ['Assurés', 'N° Police', 'Compagnie', 'Type assurance', 'Prime TTC', 'Versé', 'Reste', 'Commission', 'Taux %', 'Statut'];
            const nomCt = (r) => (r.client?.type === 'entreprise'
                ? (r.client?.raisonSociale || r.client?.nom || '')
                : `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim()) || '';

            // Regrouper par client (ordre de 1re apparition) ; le nom de l'assuré
            // sera fusionné sur toutes les lignes de ses contrats (cf. plus bas).
            const groupes = [];
            const idxParClient = new Map();
            exportContratData.forEach((r) => {
                const key = (r.client?._id || r.client || nomCt(r)).toString();
                if (!idxParClient.has(key)) { idxParClient.set(key, groupes.length); groupes.push([]); }
                groupes[idxParClient.get(key)].push(r);
            });
            groupSizes = groupes.map((g) => g.length);
            rows = [];
            groupes.forEach((groupe) => {
                groupe.forEach((r, i) => {
                    rows.push([
                        i === 0 ? nomCt(r) : '',
                        r.numeroPolice, r.compagnie?.nom || '', r.typeAssurance?.nom || '',
                        r.primeTTC, r.totalVerse, Math.max(0, r.resteAPayer),
                        r.commissionCourtier || 0, r.tauxCommission || 0,
                        `${Math.round(r.progressPaiement || 0)}%`,
                    ]);
                });
            });
        } else if (activeTab === 'par-client') {
            headers = ['Client', 'Type', 'Contrats', 'Total primes', 'Versé', 'Reste', 'Progression'];
            rows = exportClientData.map((r) => [
                `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim(),
                r.client?.type || '',
                r.nbContrats, r.totalPrimeTTC, r.totalVerse,
                Math.max(0, r.resteAPayer),
                `${Math.round(r.progressPaiement || 0)}%`,
            ]);
        } else {
            headers = ['Période', 'Nb', 'Total', 'Espèces', 'Chèque', 'Virement', 'Mobile Money'];
            rows = periodes.map((r) => [
                r.periode, r.count, r.total,
                r.parMode?.especes?.total || 0,
                r.parMode?.cheque?.total || 0,
                r.parMode?.virement?.total || 0,
                r.parMode?.mobile_money?.total || 0,
            ]);
        }
        // ─── Métadonnées : Titre + Agence/Compagnie/Période ────────────
        const agenceNom = resolveAgenceLabel({ selected: selectedAgence, agences, canSwitchAgence, userAgenceId, allLabel: 'TOUTES' });
        const compagnieNom = selectedCompagnie !== 'all'
            ? (compagnies.find((c) => c._id === selectedCompagnie)?.nom || '')
            : 'TOUTES';

        await addExcelLogoHeader(wb, ws);
        const titleRow = ws.addRow(['ÉTAT DES RÈGLEMENTS']);
        ws.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        const metaRow = ws.addRow([`Agence : ${agenceNom}   |   Compagnie : ${compagnieNom}   |   Type : ${getTypeNom()}`]);
        ws.mergeCells(metaRow.number, 1, metaRow.number, headers.length);
        metaRow.getCell(1).font = { bold: true, size: 10 };
        metaRow.getCell(1).alignment = { horizontal: 'center' };

        if (dateRange && dateRange[0] && dateRange[1]) {
            const du = dateRange[0].format('DD/MM/YYYY');
            const au = dateRange[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, headers.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: headers.length, exporterName });
        ws.addRow([]);

        const hr = ws.addRow(headers);
        hr.eachCell((c) => {
            c.font = { bold: true, color: { argb: '000000' } };
        });
        const firstDataRow = hr.number + 1;
        rows.forEach((r) => ws.addRow(r));
        // Fusion verticale du nom de l'assuré pour ses contrats groupés (par-contrat)
        if (groupSizes) {
            let rn = firstDataRow;
            groupSizes.forEach((size) => {
                if (size > 1) {
                    ws.mergeCells(rn, 1, rn + size - 1, 1);
                    ws.getCell(rn, 1).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                }
                rn += size;
            });
        }
        ws.columns.forEach((c) => { c.width = 18; });
        const numericCols = activeTab === 'par-contrat' ? [4, 5, 6, 7]
            : activeTab === 'par-client' ? [2, 3, 4, 5]
                : [1, 2, 3, 4, 5, 6];
        addExcelTotals(ws, { headerRow: hr.number, numericCols, labelCol: 1 });
        borderExcelTable(ws, hr.number, headers.length);
        const buf = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `etat_reglements_${activeTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Excel exporté');
    };

    const exportMenuItems = [
        { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, label: 'Exporter en PDF', onClick: exportPDF },
        { key: 'excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, label: 'Exporter en Excel', onClick: exportExcel },
    ];

    const taux = tauxRecouvrement;
    const tauxTone = taux >= 80 ? 'green' : taux >= 50 ? 'orange' : 'red';

    return (
        <div className="etat-reglements-page">
            {/* Header */}
            <header className="etat-header">
                <div className="etat-header-text">
                    <h1 className="etat-title">
                        <FundOutlined /> État des règlements
                    </h1>
                    <p className="etat-subtitle">Tableau de bord comptable</p>
                </div>
                <div className="etat-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        Guide
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchCurrent}
                        className="etat-btn-refresh"
                        aria-label="Rafraîchir"
                    />
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — État des règlements</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={640}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page est un <strong>tableau de bord comptable</strong> en lecture seule :
                    elle synthétise, pour chaque contrat, ce qui a été facturé, encaissé et ce qui
                    reste dû. Les paiements eux-mêmes se saisissent dans « Règlements ».
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <FundOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Les indicateurs (en haut)</strong> — Total primes (montant
                                    facturé), Total versé (encaissé), Reste à payer, Commissions et
                                    <strong> Taux de recouvrement</strong> (versé ÷ primes). Ils se
                                    recalculent selon les filtres appliqués.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <FileProtectOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Les trois vues</strong> — <strong>Par contrat</strong>
                                    (détail police par police), <strong>Par client</strong> (cumul par
                                    client), <strong>Par période</strong> (encaissements par mode de
                                    paiement sur une période).
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SearchOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Filtrer</strong> — Recherchez par client ou n° de police,
                                    puis affinez par <strong>statut</strong>, <strong>agence</strong>,
                                    <strong> compagnie</strong>, <strong>type d'assurance</strong> (risque) et <strong>période</strong>.
                                    « Effacer » réinitialise tous les filtres.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <CheckCircleOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Lire les statuts</strong> —{' '}
                                    <Tag color="green" style={{ marginInlineEnd: 0 }}>Payé</Tag> soldé à
                                    100 % ·{' '}
                                    <Tag color="gold" style={{ marginInline: 4 }}>Partiel</Tag> partiellement
                                    réglé (la colonne %) ·{' '}
                                    <Tag color="red" style={{ marginInlineStart: 4 }}>Impayé</Tag> aucun
                                    versement. La pastille « % » indique l'avancement du recouvrement.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de la vue courante, en tenant compte des filtres
                                    (agence, compagnie, type d'assurance, période) pour vos rapprochements comptables.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            {activeTab !== 'par-periode' ? (
                <section className="etat-kpis" aria-label="Indicateurs">
                    <MiniKpiCard
                        icon={<FileProtectOutlined />}
                        label="Total primes"
                        value={formatMontant(currentStats.totalPrimeTTC || 0)}
                        tone="blue"
                    />
                    <MiniKpiCard
                        icon={<CheckCircleOutlined />}
                        label="Total versé"
                        value={formatMontant(currentStats.totalVerse || 0)}
                        tone="green"
                    />
                    <MiniKpiCard
                        icon={<ExclamationCircleOutlined />}
                        label="Reste à payer"
                        value={formatMontant(currentStats.totalResteAPayer || 0)}
                        tone={currentStats.totalResteAPayer > 0 ? 'red' : 'green'}
                    />
                    {activeTab === 'par-contrat' && (
                        <MiniKpiCard
                            icon={<DollarOutlined />}
                            label="Commissions"
                            value={formatMontant(contratStats.totalCommissions || 0)}
                            tone="purple"
                        />
                    )}
                    <MiniKpiCard
                        icon={<PercentageOutlined />}
                        label="Taux recouvrement"
                        value={`${taux}%`}
                        tone={tauxTone}
                    />
                </section>
            ) : (
                <section className="etat-kpis etat-kpis-3" aria-label="Indicateurs période">
                    <MiniKpiCard
                        icon={<CheckCircleOutlined />}
                        label="Total encaissé"
                        value={formatMontant(periodeStats.totalEncaisse || 0)}
                        tone="green"
                    />
                    <MiniKpiCard
                        icon={<FileProtectOutlined />}
                        label="Nb règlements"
                        value={periodeStats.nbReglements || 0}
                        tone="blue"
                    />
                    <MiniKpiCard
                        icon={<DollarOutlined />}
                        label="Montant moyen"
                        value={formatMontant(Math.round(periodeStats.moyenneMontant || 0))}
                        tone="orange"
                    />
                </section>
            )}

            {/* Carte principale */}
            <Card className="etat-main-card">
                {/* Toolbar */}
                <div className="etat-toolbar">
                    {activeTab !== 'par-periode' && (
                        <Input
                            allowClear
                            placeholder="Rechercher (client, n° police...)"
                            prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="etat-search"
                        />
                    )}
                    {activeTab !== 'par-periode' && (
                        <Select
                            value={selectedStatut}
                            onChange={setSelectedStatut}
                            className="etat-filter"
                            aria-label="Filtrer par statut"
                        >
                            <Option value="all">Tous les statuts</Option>
                            <Option value="paye">Payé</Option>
                            <Option value="partiel">Partiel</Option>
                            <Option value="impaye">Impayé</Option>
                        </Select>
                    )}
                    {canSwitchAgence && (
                        <Select
                            value={selectedAgence}
                            onChange={setSelectedAgence}
                            className="etat-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => <Option key={a._id} value={a._id}>{a.nom}</Option>)}
                        </Select>
                    )}
                    <Select
                        value={selectedCompagnie}
                        onChange={setSelectedCompagnie}
                        className="etat-filter"
                        aria-label="Filtrer par compagnie"
                    >
                        <Option value="all">Toutes les compagnies</Option>
                        {compagnies.map((c) => <Option key={c._id} value={c._id}>{c.nom}</Option>)}
                    </Select>
                    <Select
                        value={selectedType}
                        onChange={setSelectedType}
                        className="etat-filter"
                        aria-label="Filtrer par type d'assurance"
                    >
                        <Option value="all">Tous les types d'assurance</Option>
                        {typesAssurance.map((t) => <Option key={t._id} value={t._id}>{t.nom}</Option>)}
                    </Select>
                    {activeTab === 'par-periode' && (
                        <Select
                            value={groupBy}
                            onChange={setGroupBy}
                            className="etat-filter"
                            aria-label="Regroupement"
                        >
                            <Option value="jour">Par jour</Option>
                            <Option value="semaine">Par semaine</Option>
                            <Option value="mois">Par mois</Option>
                        </Select>
                    )}
                    <RangePicker
                        format="DD/MM/YYYY"
                        onChange={setDateRange}
                        value={dateRange}
                        className="etat-filter-dates"
                        placeholder={['Date début', 'Date fin']}
                        allowClear
                    />
                    {(activeFiltersCount > 0 || search) && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="etat-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="etat-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                {/* Tabs */}
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        { key: 'par-contrat', label: <span><FileProtectOutlined /> Par contrat</span> },
                        { key: 'par-client',  label: <span><TeamOutlined /> Par client</span> },
                        { key: 'par-periode', label: <span><CalendarOutlined /> Par période</span> },
                    ]}
                    className="etat-tabs"
                />

                {/* Tables */}
                {activeTab === 'par-contrat' && (
                    <Table
                        columns={contratColumns}
                        dataSource={contratData}
                        rowKey="_id"
                        loading={contratLoading}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                        pagination={{
                            ...contratPagination,
                            onChange: (p, ps) => fetchContrats(p, ps),
                            showSizeChanger: true,
                            showTotal: (t) => `${t} contrat${t > 1 ? 's' : ''}`,
                        }}
                        summary={() => contratData.length > 0 && (
                            <Table.Summary fixed>
                                <Table.Summary.Row className="etat-summary-row">
                                    <Table.Summary.Cell index={0} colSpan={3}><strong>TOTAL</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">
                                        <strong className="etat-amount">{formatMontant(contratData.reduce((s, r) => s + (r.primeTTC || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <strong className="etat-amount is-positive">{formatMontant(contratData.reduce((s, r) => s + (r.totalVerse || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        <strong className="etat-amount is-warning">{formatMontant(contratData.reduce((s, r) => s + Math.max(0, r.resteAPayer || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">
                                        <strong className="etat-amount is-purple">{formatMontant(contratData.reduce((s, r) => s + (r.commissionCourtier || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={7} colSpan={2} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}

                {activeTab === 'par-client' && (
                    <Table
                        columns={clientColumns}
                        dataSource={clientData}
                        rowKey={(r) => r.client?._id || r._id}
                        loading={clientLoading}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                        onRow={(record) => ({
                            onClick: () => navigate(`/dashboard/detail-reglement/${record.client?._id}`),
                            style: { cursor: 'pointer' },
                        })}
                        pagination={{
                            ...clientPagination,
                            onChange: (p, ps) => fetchClients(p, ps),
                            showSizeChanger: true,
                            showTotal: (t) => `${t} client${t > 1 ? 's' : ''}`,
                        }}
                    />
                )}

                {activeTab === 'par-periode' && (
                    <Table
                        columns={periodeColumns}
                        dataSource={periodes}
                        rowKey="periode"
                        loading={periodeLoading}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                        pagination={false}
                        summary={() => periodes.length > 0 && (
                            <Table.Summary fixed>
                                <Table.Summary.Row className="etat-summary-row">
                                    <Table.Summary.Cell index={0}><strong>TOTAL</strong></Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="center">
                                        <strong>{periodes.reduce((s, r) => s + r.count, 0)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right">
                                        <strong className="etat-amount is-positive">{formatMontant(periodes.reduce((s, r) => s + r.total, 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">
                                        <strong>{formatMontant(periodes.reduce((s, r) => s + (r.parMode?.especes?.total || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <strong>{formatMontant(periodes.reduce((s, r) => s + (r.parMode?.cheque?.total || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="right">
                                        <strong>{formatMontant(periodes.reduce((s, r) => s + (r.parMode?.virement?.total || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6} align="right">
                                        <strong>{formatMontant(periodes.reduce((s, r) => s + (r.parMode?.mobile_money?.total || 0), 0))}</strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                )}
            </Card>
        </div>
    );
};

export default EtatReglements;
